import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Stack,
  Button,
  Paper,
  Tooltip
} from '@mui/material';
import { DeleteOutline, PhotoCamera } from '@mui/icons-material';
import palette from 'src/theme/palette';

const StagedImagesTab = ({
  stagedImages = [],
  onUploadStagedImages,
  onRemoveStagedImage,
  loadingProperty = false
}) => {
  const handlePairImageUpload = (event, index) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const preview = URL.createObjectURL(file);
      onUploadStagedImages(event, index, {
        paired: { file, preview, name: file.name }
      });
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Staged Images
      </Typography>

      {/* Main multiple upload */}
      <input
        accept="image/*"
        id="staged-images-upload"
        multiple
        type="file"
        style={{ display: 'none' }}
        onChange={onUploadStagedImages}
        disabled={loadingProperty}
      />
      <label htmlFor="staged-images-upload">
        <Button
          variant="contained"
          component="span"
          startIcon={<PhotoCamera />}
          sx={{ background: palette.primary.main }}
          disabled={loadingProperty}
        >
          Upload Staged Images
        </Button>
      </label>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        Upload main images and their paired/staged versions
      </Typography>

      {/* Images Grid */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {stagedImages.length === 0 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {loadingProperty ? 'Loading staged images...' : 'No staged images uploaded yet.'}
              </Typography>
            </Paper>
          </Grid>
        )}

        {stagedImages.map((image, index) => (
          <Grid item xs={12} md={12} lg={6} key={index}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Staged Image Pair {index + 1}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    minHeight: 200,
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}
                >
                  {/* Main Image */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.black"
                      fontWeight={600}
                      fontSize={14}
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Original Image
                    </Typography>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        borderColor: image.uploadError ? 'error.main' : 'divider'
                      }}
                    >
                      {/* Fancybox for main image */}
                      <a
                        href={image.preview || image.serverUrl}
                        data-fancybox="staged-images-gallery"
                        data-caption={`Original: ${image.name || `Staged Image ${index + 1}`}`}
                      >
                        <Box
                          component="img"
                          src={image.preview || image.serverUrl}
                          alt={image.name}
                          sx={{
                            width: '100%',
                            height: 160,
                            objectFit: 'cover',
                            opacity: image.uploadError ? 0.5 : 1,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.02)'
                            }
                          }}
                        />
                      </a>
                      {image.uploadError && (
                        <Box sx={{ p: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
                          <Typography variant="caption">Upload Failed</Typography>
                        </Box>
                      )}
                    </Card>
                    <Typography variant="body2" noWrap sx={{ mt: 1 }}>
                      {image.name}
                    </Typography>
                  </Box>

                  {/* Paired Image */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.black"
                      fontWeight={600}
                      fontSize={14}
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Staged/Paired Image
                    </Typography>
                    {image.paired ? (
                      <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                        {/* Fancybox for paired image */}
                        <a
                          href={image.paired.preview || image.paired.serverUrl}
                          data-fancybox="staged-images-gallery"
                          data-caption={`Staged: ${
                            image.paired.name || `Paired Image ${index + 1}`
                          }`}
                        >
                          <Box
                            component="img"
                            src={image.paired.preview || image.paired.serverUrl}
                            alt={image.paired.name}
                            sx={{
                              width: '100%',
                              height: 160,
                              objectFit: 'cover',
                              opacity: image.paired.uploadError ? 0.5 : 1,
                              transition: 'transform 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'scale(1.02)'
                              }
                            }}
                          />
                        </a>
                        {image.paired.uploadError && (
                          <Box sx={{ p: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
                            <Typography variant="caption">Upload Failed</Typography>
                          </Box>
                        )}
                      </Card>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          height: 160,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderStyle: 'dashed',
                          bgcolor: 'grey.50'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" align="center">
                          No paired image
                        </Typography>
                      </Paper>
                    )}

                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        accept="image/*"
                        id={`pair-image-upload-${index}`}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={(e) => handlePairImageUpload(e, index)}
                        disabled={loadingProperty}
                      />
                      <label htmlFor={`pair-image-upload-${index}`}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="span"
                          startIcon={<PhotoCamera />}
                          disabled={loadingProperty}
                        >
                          {image.paired ? 'Replace' : 'Add Pair'}
                        </Button>
                      </label>

                      {image.paired && (
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {image.paired.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Actions */}
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Tooltip title="Remove this staged image pair">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveStagedImage(index)}
                      disabled={loadingProperty}
                      color="error"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default StagedImagesTab;
